import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  BedDouble,
  Building2,
  DoorOpen,
  Gauge,
  Users,
} from "lucide-react";
import {
  fetchAdminCoLivingMetrics,
  fetchAdminCoLivingUpcoming,
  fetchAdminUnitsAll,
  fetchAdminRooms,
  fetchAdminOccupancy,
  fetchAdminProfit,
  fetchAdminTenanciesAll,
  normalizeUnit,
  normalizeRoom,
} from "../../api/adminData";
import CoLivingWarningsCard from "./coLiving/CoLivingWarningsCard";
import CoLivingUpcomingMovesCard from "./coLiving/CoLivingUpcomingMovesCard";
import CoLivingKpiCard from "./coLiving/CoLivingKpiCard";
import CoLivingOccupancyOverview from "./coLiving/CoLivingOccupancyOverview";
import CoLivingUnitsOverview, {
  buildUnitOverviewRows,
} from "./coLiving/CoLivingUnitsOverview";
import CoLivingRoomStatusDonut from "./coLiving/CoLivingRoomStatusDonut";
import CoLivingMonthlyForecast from "./coLiving/CoLivingMonthlyForecast";
import {
  getRoomOccupancyStatus,
  isActiveRoom,
} from "../../utils/unitOccupancyStatus";
import { occupancyPercentText } from "../../utils/occupancyPercent";
import {
  formatDateOnly,
  todayBusinessDate,
  currentBusinessPeriod,
} from "../../utils/datetime";
import {
  getUnitHeadingLabel,
  normalizeUnitTypeLabel,
} from "../../utils/unitDisplayId";

function sumFilteredProfitField(profitResponse, filteredUnits, field) {
  if (!profitResponse?.units || !Array.isArray(profitResponse.units)) return null;
  const allowed = new Set(
    filteredUnits.map((u) => String(u.id ?? u.unitId))
  );
  let sum = 0;
  for (const row of profitResponse.units) {
    if (allowed.has(String(row.unit_id))) {
      sum += Number(row[field] ?? 0);
    }
  }
  return sum;
}

function profitRowsByUnitId(profitResponse) {
  const m = new Map();
  if (!profitResponse?.units) return m;
  for (const row of profitResponse.units) {
    m.set(String(row.unit_id), row);
  }
  return m;
}

/**
 * "Ø Umsatz pro Room" for the SELECTED MONTH, over the units that actually
 * operate in it (Slice 9B).
 *
 * REVENUE, NOT RENT. The numerator is the broad cashflow `revenue` the
 * co-living-metrics contract already publishes — recurring rent plus every
 * other recurring position plus one-time revenue. A one-time fee raising this
 * KPI is correct: the card says "Umsatz". The rent-only question is answered
 * by `expected_recurring_rent` and `vacancy_loss` on the same contract, and
 * the two must not be mixed.
 *
 * BOTH HALVES FROM ONE SOURCE. Before Slice 9B the numerator came from
 * `/api/admin/profit` and the denominator from the metrics room count — two
 * endpoints, two populations, one quotient. Now both come from the SAME
 * per-unit, per-month `co-living-metrics` response, so they cannot describe
 * different sets.
 *
 * THE DENOMINATOR IS NOT A ROOM COUNT. An inventory room that the unit does
 * not operate for half the month is not one room of earning capacity. The
 * canonical Slice 8B `period_capacity` already answers this in room-days, so:
 *
 *     equivalent_operating_rooms = Σ available_room_days / days_in_month
 *
 * Four rooms operating all 30 days is 120/30 = 4. The same four rooms from the
 * 11th onward is 80/30 = 2.67 — fewer equivalent rooms, so the same revenue
 * reads as a higher average per room, which is the point.
 *
 * `days_in_month` is taken from the contract rather than computed: every unit
 * here is priced for the same selected month, so re-deriving a calendar length
 * in the browser would be a second month truth able to disagree with the one
 * the number was built from.
 *
 * FAIL CLOSED. Returns null — the card shows a dash — when any filtered unit
 * has not answered, or has answered without a usable `period_capacity`. A
 * partial load is a technical state, not a business average, and quietly
 * dropping the units that failed would publish a number for a population
 * nobody chose. A unit that answered with zero capacity is NOT a failure: it
 * is a unit that does not operate this month, and it contributes nothing to
 * either half — including its revenue, which stays visible in the raw contract
 * but has no operating room to be an average over.
 */
function aggregateAverageRevenuePerOperatingRoom(
  filteredUnits,
  coLivingMetricsByUnitId
) {
  let availableRoomDays = 0;
  let periodRevenue = 0;
  let daysInMonth = null;

  for (const unit of filteredUnits) {
    const uid = String(unit.id ?? unit.unitId);
    const clm = coLivingMetricsByUnitId[uid];
    if (!clm) return null; // still loading, or the request failed
    const pc = clm.period_capacity;
    if (!pc) return null; // answered, but without the canonical block

    const unitRoomDays = Number(pc.available_room_days);
    const unitDays = Number(pc.days_in_month);
    if (!Number.isFinite(unitRoomDays) || unitRoomDays < 0) return null;
    if (!Number.isFinite(unitDays) || unitDays <= 0) return null;
    // All units are priced for the same month, so a disagreement here is a
    // contract fault, not a case to average over.
    if (daysInMonth === null) daysInMonth = unitDays;
    else if (daysInMonth !== unitDays) return null;

    if (unitRoomDays === 0) continue; // not operating this month: neither half

    availableRoomDays += unitRoomDays;
    const unitRevenue = Number(clm.revenue);
    if (!Number.isFinite(unitRevenue)) return null;
    periodRevenue += unitRevenue;
  }

  if (daysInMonth === null) return null; // nothing in the filtered population
  const equivalentOperatingRooms = availableRoomDays / daysInMonth;
  return {
    periodRevenue,
    availableRoomDays,
    equivalentOperatingRooms,
    averageRevenuePerOperatingRoom:
      equivalentOperatingRooms > 0
        ? periodRevenue / equivalentOperatingRooms
        : null,
  };
}

/**
 * Sum GET /api/admin/occupancy `units[]` rows whose unit_id is in filtered
 * Co-Living units (same scope as the page).
 *
 * `unitsCount` is the CURRENT OPERATING unit count: how many of the filtered
 * units the authoritative endpoint actually answered for. The server applies
 * `units_in_portfolio(units, day)` (occupancy_service.py:122) before building
 * that list, so a unit whose lease window does not cover today is simply
 * absent from it — counting the matched rows inherits that gate instead of
 * recomputing it. `filteredUnits.length` is the INVENTORY count and answers a
 * different question; the two are kept apart deliberately.
 *
 * `fullUnits` / `partialUnits` are CURRENT STATE, counted from the same rows
 * and therefore behind the same server-side lease gate. They used to be
 * counted from the month-scoped `co-living-metrics` endpoint, which is a
 * different clock (tenancy overlap with the billing month) and has no lease
 * gate at all — so a unit whose lease had ended still reported room counts,
 * and a room vacated mid-month still read as occupied for the whole month.
 * The two predicates themselves are unchanged; only their input moved to the
 * canonical source. A stored-`reserved` tenancy whose move-in date has passed
 * counts as OCCUPIED here, because the server resolves lifecycle state against
 * the day (`effective_tenancy_status`, tenancy_state.py:33-60) rather than
 * trusting the stored enum.
 */
function aggregateOccupancyForFilter(occupancyApi, filteredUnits) {
  if (!occupancyApi?.units || !Array.isArray(occupancyApi.units)) return null;
  const allowed = new Set(
    filteredUnits.map((u) => String(u.id ?? u.unitId))
  );
  let unitsCount = 0;
  let totalRooms = 0;
  let occupiedRooms = 0;
  let reservedRooms = 0;
  let freeRooms = 0;
  let fullUnits = 0;
  let partialUnits = 0;
  for (const row of occupancyApi.units) {
    if (!allowed.has(String(row.unit_id))) continue;
    unitsCount += 1;
    const rowTotalRooms = Number(row.total_rooms ?? 0);
    const rowOccupiedRooms = Number(row.occupied_rooms ?? 0);
    totalRooms += rowTotalRooms;
    occupiedRooms += rowOccupiedRooms;
    reservedRooms += Number(row.reserved_rooms ?? 0);
    freeRooms += Number(row.free_rooms ?? 0);
    const isFullyOccupied = rowTotalRooms > 0 && rowOccupiedRooms === rowTotalRooms;
    const isPartiallyOccupied = rowOccupiedRooms > 0 && rowOccupiedRooms < rowTotalRooms;
    if (isFullyOccupied) fullUnits += 1;
    if (isPartiallyOccupied) partialUnits += 1;
  }
  const occupiedRate =
    totalRooms > 0 ? (occupiedRooms / totalRooms) * 100 : 0;
  const reservedRate =
    totalRooms > 0 ? (reservedRooms / totalRooms) * 100 : 0;
  const freeRate = totalRooms > 0 ? (freeRooms / totalRooms) * 100 : 0;
  const round1 = (n) => Math.round(n * 10) / 10;
  return {
    unitsCount,
    totalRooms,
    occupiedRooms,
    reservedRooms,
    freeRooms,
    fullUnits,
    partialUnits,
    occupiedRate: round1(occupiedRate),
    reservedRate: round1(reservedRate),
    freeRate: round1(freeRate),
  };
}

/**
 * Shown wherever an operating-population value would otherwise stand. NOT a
 * zero: zero is a measurement ("no rooms are free"), this is the absence of a
 * measurement ("we cannot say"). The two must not look alike.
 */
const OPERATING_DASH = "—";

/**
 * The swatch a stood-down room wears — a hollow ring, not a third grey.
 *
 * ROOM-ACT-5 gave it `bg-muted-foreground/60`, the SAME disc the unknown tile
 * already used, on the reasoning that neither state is a claim about
 * lettability. That reasoning still holds for the card tone, but OC4c put both
 * words in the legend, and two identical swatches in a five-entry key tell the
 * reader nothing about which is which.
 *
 * So the distinction is SHAPE, following `calendar/CalendarLegend.jsx`, which
 * reaches for a border rather than a colour for exactly this reason ("the only
 * thing this entry can honestly show is the SHAPE"). A 1px ring carries less
 * ink than the filled disc it replaces, so the quietest state does not become
 * the loudest — and shape reads for someone who cannot separate two greys.
 *
 * Existing semantic token, no fill: the ring is `--muted-foreground` at the
 * same 60% the disc used, so neither theme needs a second decision.
 */
const INACTIVE_DOT = "border border-muted-foreground/60";
const OPERATING_UNAVAILABLE_NOTE =
  "Betriebszahlen aktuell nicht verfügbar — Belegung wird nicht aus Bestandszahlen geschätzt.";

function roundCurrency(value) {
  return Math.round(Number(value || 0));
}

function formatCurrency(value) {
  return `CHF ${roundCurrency(value).toLocaleString("de-CH")}`;
}

function formatChfOrDash(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "-";
  }
  return formatCurrency(value);
}

function getTodayDateString() {
  return todayBusinessDate();
}

/**
 * Local-midnight anchor of the CURRENT SWISS business month.
 *
 * Every month value on this page is such an anchor: `addMonths`,
 * `startOfMonth` and `getMonthEnd` all build and read them with
 * LOCAL getters, so the representation is self-consistent and deliberately
 * unchanged here. The only thing that was wrong was where "current" came
 * from: the device's own clock names the DEVICE's month, and on 31 August at
 * 22:30Z Switzerland is already in September while a browser in UTC is not.
 *
 * So: the Swiss business period decides WHICH month, and the anchor is then
 * built in exactly the shape the rest of this file already expects.
 *
 * Exported for tests — the month boundary is the whole point and it cannot be
 * reached through the UI without waiting for one.
 */
export function currentBusinessMonthStart(now) {
  const { year, month } = currentBusinessPeriod(now);
  return new Date(year, month - 1, 1);
}

/**
 * The month the dashboard is showing, as a local-midnight anchor.
 *
 * `customMonth` is the user's own choice and is parsed verbatim — the Swiss
 * clock decides what "this/last/next" mean, never what an explicit selection
 * means.
 *
 * Exported for tests.
 */
export function getSelectedMonthDate(selectedPeriod, selectedMonth, now) {
  const current = currentBusinessMonthStart(now);

  if (selectedPeriod === "lastMonth") {
    return new Date(current.getFullYear(), current.getMonth() - 1, 1);
  }

  if (selectedPeriod === "thisMonth") {
    return new Date(current.getFullYear(), current.getMonth(), 1);
  }

  if (selectedPeriod === "nextMonth") {
    return new Date(current.getFullYear(), current.getMonth() + 1, 1);
  }

  if (selectedPeriod === "customMonth" && selectedMonth) {
    const [year, month] = selectedMonth.split("-");
    return new Date(Number(year), Number(month) - 1, 1);
  }

  return new Date(current.getFullYear(), current.getMonth(), 1);
}

function addMonths(date, months) {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function hasLeaseStarted(unit) {
  const c = String(unit?.leaseStartDate ?? unit?.lease_start_date ?? "").trim();
  if (c && /^\d{4}-\d{2}-\d{2}/.test(c)) {
    return c.slice(0, 10) <= getTodayDateString();
  }
  const af = String(unit?.availableFrom ?? "").trim();
  if (af && /^\d{4}-\d{2}-\d{2}/.test(af)) {
    return af.slice(0, 10) <= getTodayDateString();
  }
  return false;
}

function getMonthStart(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getMonthEnd(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function getRoomsForUnit(unitId, allRooms = []) {
  return allRooms.filter((room) => (room.unitId || room.unit_id) === unitId);
}

/**
 * One labelled control in the dashboard's filter bar.
 *
 * The <label> stays a sibling of the <select> — the same structure the
 * population net locates the Unit filter by (its "Alle Units" sentinel), so
 * that reader keeps working. Tokens only; the native control follows the theme
 * because `color-scheme` is declared per theme root in index.css.
 */
function FilterSelect({ label, value, onChange, children }) {
  return (
    <div className="flex min-w-[150px] flex-col gap-1">
      <label className="text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
        {label}
      </label>
      <select
        value={value}
        onChange={onChange}
        className="w-full cursor-pointer rounded-[8px] border border-border bg-card px-2.5 py-1.5 text-[12px] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {children}
      </select>
    </div>
  );
}

function AdminCoLivingDashboardPage() {
  const [selectedPeriod, setSelectedPeriod] = useState("thisMonth");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedPlace, setSelectedPlace] = useState("all");
  const [selectedUnitId, setSelectedUnitId] = useState("all");
  const [period, setPeriod] = useState("month");

  const activeMonth = useMemo(() => {
    return getSelectedMonthDate(selectedPeriod, selectedMonth);
  }, [selectedPeriod, selectedMonth]);

  const [units, setUnits] = useState([]);
  // Has the unit request SETTLED? `units` cannot answer that: it starts as []
  // and an org with no Co-Living units also ends as []. The moves card needs
  // the difference, because its whole population filter is derived from this
  // list — showing "Keine geplanten Ein- oder Auszüge." while the roster is
  // still in flight would be the same false-calm this dashboard has been
  // removing since Slice 7. Set in BOTH branches: a failed load is a settled
  // answer too, just an empty one.
  const [unitsLoaded, setUnitsLoaded] = useState(false);
  const [rooms, setRooms] = useState([]);
  // `null` means NOT ANSWERED — either still loading or the request failed.
  // An empty array is a real answer ("this org has no tenancies") and must stay
  // distinguishable from it: `getRoomOccupancyStatus` returns null for null and
  // "frei" for [], and that distinction is the whole point of this state.
  //
  // The initial value used to be [], so every room read "Frei" for the duration
  // of the first request as well as forever after a failure.
  const [tenancies, setTenancies] = useState(null);
  const [tenanciesError, setTenanciesError] = useState("");
  const [occupancyApi, setOccupancyApi] = useState(null);
  const [profitSixMonth, setProfitSixMonth] = useState(null);
  const [upcomingSnapshot, setUpcomingSnapshot] = useState(null);
  const [upcomingError, setUpcomingError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const periods = Array.from({ length: 6 }, (_, index) => {
      const d = addMonths(startOfMonth(activeMonth), index - 2);
      return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });
    Promise.all(
      periods.map((p) => fetchAdminProfit(p).catch(() => null))
    )
      .then((arr) => {
        if (!cancelled) setProfitSixMonth(arr);
      });
    return () => {
      cancelled = true;
    };
  }, [activeMonth]);

  useEffect(() => {
    // `fetchAdminUnitsAll`, not `fetchAdminUnits`: the latter sends no query
    // string and therefore takes the server default of 50 rows
    // (`limit: int = Query(50, ge=1, le=200)`), silently. Rows come back
    // ordered by `Unit.title, Unit.id`, so past 50 org units it is the
    // alphabetically-last titles that disappear — from a roster this page
    // presents as the whole portfolio. The helper pages until exhausted and
    // throws rather than returning a partial list, so a failed later page
    // lands in the `catch` below instead of masquerading as a complete answer.
    fetchAdminUnitsAll()
      .then((data) => {
        setUnits(Array.isArray(data) ? data.map(normalizeUnit) : []);
        setUnitsLoaded(true);
      })
      .catch(() => {
        setUnits([]);
        setUnitsLoaded(true);
      });
    fetchAdminRooms()
      .then((data) => setRooms(Array.isArray(data) ? data.map(normalizeRoom) : []))
      .catch(() => setRooms([]));
    fetchAdminTenanciesAll()
      .then((data) => {
        setTenancies(Array.isArray(data) ? data : []);
        setTenanciesError("");
      })
      .catch((e) => {
        // A dead network is not a business fact. Leaving `tenancies` at null
        // keeps the room grid on its unknown branch instead of reporting every
        // room as available.
        console.warn("Tenancy load failed:", e);
        setTenancies(null);
        setTenanciesError(
          e?.message || "Belegungsstatus konnte nicht geladen werden."
        );
      });
    fetchAdminOccupancy()
      .then((data) => setOccupancyApi(data))
      .catch(() => setOccupancyApi(null));
  }, []);

  // Slice 2: die Warnkarte liest den typisierten Co-Living-Contract statt des
  // generischen Alert-Service. `/api/admin/alerts` kennt nur partial_vacancy und
  // hat keinen Vorausblick, konnte eine bevorstehende Kuendigung also gar nicht
  // melden. Der Endpoint ist org-scoped und braucht keinen Stichtag.
  useEffect(() => {
    let cancelled = false;
    fetchAdminCoLivingUpcoming()
      .then((json) => {
        if (cancelled) return;
        setUpcomingSnapshot(json);
        setUpcomingError("");
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn("Co-Living upcoming load failed:", e);
        setUpcomingError(
          e.message || "Co-Living Warnungen konnten nicht geladen werden."
        );
        setUpcomingSnapshot(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // WHICH units are Co-Living is unchanged by this predicate — only how
  // reliably the same answer is reached. `unit.type` is the raw wire string
  // (`normalizeUnit` spreads it through untouched), so an exact `===` dropped
  // every unit whose type differs only in spacing or in which Unicode dash it
  // uses: "Co Living", "coliving", and a U+2011 non-breaking hyphen that is
  // indistinguishable from "-" on screen. `normalizeUnitTypeLabel` is the
  // canonical fold the repo already classifies with — `PortfolioMapSection`'s
  // `isCoLivingType` and `unitOccupancyStatus` both branch on exactly this
  // comparison — not a new heuristic invented here.
  const allCoLivingUnits = useMemo(() => {
    return units.filter(
      (unit) => normalizeUnitTypeLabel(unit.type) === "Co-Living"
    );
  }, [units]);

  const placeOptions = useMemo(() => {
    const places = [
      ...new Set(allCoLivingUnits.map((unit) => unit.place).filter(Boolean)),
    ];
    return places.sort((a, b) => a.localeCompare(b));
  }, [allCoLivingUnits]);

  const filteredUnits = useMemo(() => {
    return allCoLivingUnits.filter((unit) => {
      const placeOk = selectedPlace === "all" || unit.place === selectedPlace;
      const unitOk = selectedUnitId === "all" || unit.unitId === selectedUnitId;
      return placeOk && unitOk;
    });
  }, [allCoLivingUnits, selectedPlace, selectedUnitId]);

  const firstFilteredUnit = useMemo(() => filteredUnits[0], [filteredUnits]);

  /**
   * The population "Nächste Ein- und Auszüge" is allowed to show, as a set of
   * INTERNAL unit ids.
   *
   * `String(u.id ?? u.unitId)` is the same join key this page already uses
   * against `/api/admin/profit` (`sumFilteredProfitField`) and
   * `/api/admin/occupancy` (`aggregateOccupancyForFilter`), and the same id the
   * upcoming contract emits (`"unit_id": str(t.unit_id)`). No second filter
   * predicate is introduced: place and unit selection are already applied by
   * `filteredUnits`, and this only projects it onto the key the events carry.
   *
   * Deliberately NOT `unit_short_id`: a display id is a label, and two units
   * may legitimately carry the same one. Their internal ids never collide.
   *
   * `null` until the roster has settled — see `unitsLoaded`. An empty Set is a
   * real answer ("nothing in this filter"), null is "we cannot say yet", and
   * the card renders those two differently.
   */
  const upcomingMoveUnitIds = useMemo(() => {
    if (!unitsLoaded) return null;
    return new Set(filteredUnits.map((u) => String(u.id ?? u.unitId)));
  }, [unitsLoaded, filteredUnits]);

  const profitForActiveMonth = useMemo(
    () => (profitSixMonth?.length === 6 ? profitSixMonth[2] : null),
    [profitSixMonth]
  );

  const profitByUnitIdActive = useMemo(
    () => profitRowsByUnitId(profitForActiveMonth),
    [profitForActiveMonth]
  );

  // Step 4.3: load Co-Living metrics from backend instead of computing locally.
  const [coLivingMetricsByUnitId, setCoLivingMetricsByUnitId] = useState({});
  useEffect(() => {
    if (!filteredUnits.length) {
      setCoLivingMetricsByUnitId({});
      return;
    }
    const monthStr = `${activeMonth.getFullYear()}-${String(activeMonth.getMonth() + 1).padStart(2, "0")}`;
    let cancelled = false;
    Promise.all(
      filteredUnits.map((u) =>
        fetchAdminCoLivingMetrics(String(u.id ?? u.unitId), monthStr)
          .then((data) => [String(u.id ?? u.unitId), data])
          .catch(() => [String(u.id ?? u.unitId), null])
      )
    ).then((pairs) => {
      if (cancelled) return;
      const m = {};
      for (const [uid, data] of pairs) {
        if (data) m[uid] = data;
      }
      setCoLivingMetricsByUnitId(m);
    });
    return () => { cancelled = true; };
  }, [filteredUnits, activeMonth]);

  const dashboard = useMemo(() => {
    const totals = {
      unitsCount: filteredUnits.length,
      totalRooms: 0,
      occupiedRooms: 0,
      reservedRooms: 0,
      freeRooms: 0,
      fullRevenue: null,
      currentRevenue: null,
      runningCosts: null,
      vacancyLoss: null,
      currentProfit: null,
      // `fullUnits` / `partialUnits` deliberately do NOT live here. They are
      // current-state counts and are produced by `aggregateOccupancyForFilter`
      // from the canonical occupancy rows. Leaving them off this object means
      // a render site that forgets to read `dashboardDisplay.operating` gets
      // `undefined` rather than a month-scoped number wearing a today label.
      notStartedUnits: 0,
      vacancyDays: null,
      lostRevenue7Days: null,
    };

    let totalRev = 0;
    let totalCost = 0;
    let totalProf = 0;

    // The loop below exists for its SIDE EFFECTS only: the `totals.*` room
    // counters, `notStartedUnits`, and the three profit accumulators. It used
    // to also build a per-unit row array (`unitPerformance`) carrying
    // occupancyRate, currentRevenue, currentProfit, runningCosts, vacancyLoss,
    // fullRevenue, breakEvenRevenue and breakEvenGap. Nothing ever read those
    // rows: their only consumer was `rankedUnits`, whose only consumers were
    // `bestUnit` and `worstUnit`, which no render path on this page reads. The
    // rows are gone; every statement that writes to an aggregate is unchanged
    // and in the same order.
    filteredUnits.forEach((unit) => {
      const uid = String(unit.id ?? unit.unitId);
      // Step 4.3: room counts + revenue from backend Co-Living metrics endpoint.
      const clm = coLivingMetricsByUnitId[uid] || null;
      const prow = profitByUnitIdActive.get(uid);

      if (prow != null) {
        totalRev += Number(prow.revenue);
        totalCost += Number(prow.costs);
        totalProf += Number(prow.profit);
      }

      const totalRoomsUnit = clm ? clm.total_rooms : Number(unit.rooms || 0);
      const occupiedCount = clm ? clm.occupied_count : 0;
      const reservedCount = clm ? clm.reserved_count : 0;
      const freeCount = clm ? clm.free_count : totalRoomsUnit;

      totals.totalRooms += totalRoomsUnit;
      totals.occupiedRooms += occupiedCount;
      totals.reservedRooms += reservedCount;
      totals.freeRooms += freeCount;

      if (!hasLeaseStarted(unit)) totals.notStartedUnits += 1;
    });

    totals.currentRevenue =
      profitForActiveMonth != null ? totalRev : null;
    totals.runningCosts =
      profitForActiveMonth != null ? totalCost : null;
    totals.currentProfit =
      profitForActiveMonth != null ? totalProf : null;

    const occupiedRate =
      totals.totalRooms > 0
        ? (totals.occupiedRooms / totals.totalRooms) * 100
        : 0;
    const reservedRate =
      totals.totalRooms > 0
        ? (totals.reservedRooms / totals.totalRooms) * 100
        : 0;
    const freeRate =
      totals.totalRooms > 0 ? (totals.freeRooms / totals.totalRooms) * 100 : 0;

    // Slice 9B: the average no longer divides a profit-endpoint sum by an
    // inventory room count. Both halves come from the same month-scoped
    // contract, and the denominator is operating room-days — see
    // `aggregateAverageRevenuePerOperatingRoom`.
    const perOperatingRoom = aggregateAverageRevenuePerOperatingRoom(
      filteredUnits,
      coLivingMetricsByUnitId
    );
    const averageRevenuePerRoom =
      perOperatingRoom?.averageRevenuePerOperatingRoom ?? null;
    const averageProfitPerUnit =
      filteredUnits.length > 0 && totals.currentProfit != null
        ? totals.currentProfit / filteredUnits.length
        : null;

    return {
      ...totals,
      occupiedRate,
      reservedRate,
      freeRate,
      averageRevenuePerRoom,
      // The two halves the average is built from, published beside it so the
      // number can be audited without re-deriving it. Null together with the
      // average whenever the aggregation failed closed.
      periodRevenueForP8: perOperatingRoom?.periodRevenue ?? null,
      availableRoomDays: perOperatingRoom?.availableRoomDays ?? null,
      equivalentOperatingRooms:
        perOperatingRoom?.equivalentOperatingRooms ?? null,
      averageProfitPerUnit,
    };
  }, [
    filteredUnits,
    coLivingMetricsByUnitId,
    profitByUnitIdActive,
    profitForActiveMonth,
  ]);

  const dashboardDisplay = useMemo(() => {
    const base = dashboard;
    const agg = aggregateOccupancyForFilter(occupancyApi, filteredUnits);
    // `operating` is the CURRENT OPERATING truth and exists only when the
    // authoritative endpoint answered. Null is not an error state to render as
    // zero — it means the question cannot be answered right now, and every
    // surface that claims to show the operating population reads THIS object
    // rather than the spread below. That is the whole point: a site that
    // forgets the check reads `undefined`, not an inventory number wearing an
    // operating label.
    //
    // The spread fields below kept their previous meaning (aggregate when
    // available, base otherwise) for one consumer: the forecast occupancy
    // rate. Slice 7 moved that consumer onto `operating` as well, because the
    // "base otherwise" half is precisely the silent month fallback it had to
    // stop doing. The spread now has NO readers left — every surface goes
    // through `operating`. It is kept as-is rather than deleted because this
    // slice changes sources, not shapes; anything that starts reading
    // `dashboardDisplay.occupiedRate` again is reopening that fallback.
    if (agg == null) return { ...base, operating: null };
    return {
      ...base,
      operating: agg,
      totalRooms: agg.totalRooms,
      occupiedRooms: agg.occupiedRooms,
      reservedRooms: agg.reservedRooms,
      freeRooms: agg.freeRooms,
      occupiedRate: agg.occupiedRate,
      reservedRate: agg.reservedRate,
      freeRate: agg.freeRate,
    };
  }, [dashboard, occupancyApi, filteredUnits]);

  /**
   * The current OPERATING truth, or null when the authoritative endpoint has
   * not answered. Every surface in the redesign reads THIS, never the spread
   * beside it: a site that forgets the check gets `undefined` rather than an
   * inventory number wearing an operating label.
   */
  const operating = dashboardDisplay.operating;

  // The selected month's money, plus the count of units running at a loss in
  // it. All four come from ONE profit window over the filtered population.
  //
  // TURNED by Slice 18: this memo also published `expectedOccupancyRate`, which
  // fed a "Belegung (%)" card. That number is `operating.occupiedRate` — the
  // same value the "Auslastung" KPI now shows — so the card was the same
  // measurement twice under two names. The field is gone with the card; the
  // occupancy truth itself is untouched and still comes from the authoritative
  // endpoint via `dashboardDisplay.operating`.
  const forecast = useMemo(() => {
    if (!profitForActiveMonth?.units) {
      return {
        forecastRevenue: null,
        forecastCosts: null,
        forecastProfit: null,
        // `null`, not 0 — the three money fields beside it already say "we do
        // not know" this way, and this count owes the reader the same answer.
        // A literal 0 here reads as "no unit is critical", which is a claim the
        // page cannot make without the profit rows: a failed or still-pending
        // request would quietly report an all-clear.
        //
        // ZERO IS STILL A REAL ANSWER. When the rows DO arrive and none has a
        // negative profit, the count below is 0 and renders as 0. Only the
        // unknown case moved.
        criticalUnits: null,
      };
    }
    const forecastRevenue = sumFilteredProfitField(
      profitForActiveMonth,
      filteredUnits,
      "revenue"
    );
    const forecastCosts = sumFilteredProfitField(
      profitForActiveMonth,
      filteredUnits,
      "costs"
    );
    const forecastProfit = sumFilteredProfitField(
      profitForActiveMonth,
      filteredUnits,
      "profit"
    );
    let criticalUnits = 0;
    const allowed = new Set(
      filteredUnits.map((u) => String(u.id ?? u.unitId))
    );
    for (const row of profitForActiveMonth.units) {
      if (!allowed.has(String(row.unit_id))) continue;
      if (Number(row.profit) < 0) criticalUnits += 1;
    }
    return {
      forecastRevenue,
      forecastCosts,
      forecastProfit,
      criticalUnits,
    };
  }, [profitForActiveMonth, filteredUnits]);

  /**
   * The six-month money series the chart draws: Umsatz as bars, Gewinn as a
   * line, one point per window.
   *
   * THIS IS NOT AN OCCUPANCY FORECAST, and there is no contract for one. The
   * design reference shows future occupied/free room bars and a forecast
   * occupancy rate; Vantio publishes no reserved-room-days, no future room
   * counts and no forward occupancy rate, so drawing them would mean inventing
   * them. Both series here are CHF from `/api/admin/profit`, the same windows
   * and the same `sumFilteredProfitField` the page has always used.
   *
   * `null` per month, never 0, when a window is missing or failed: the bar is
   * absent and the line breaks (`connectNulls={false}`) instead of a month
   * sitting on the axis looking like it earned nothing.
   */
  const monthlySeries = useMemo(() => {
    const monthLabel = (index) =>
      addMonths(startOfMonth(activeMonth), index - 2).toLocaleDateString("de-CH", {
        month: "short",
      });
    // Index 2 IS the selected month — the window order is fixed at two back,
    // selected, three ahead. The flag is published rather than recomputed in
    // the card so the card never has to know the window layout.
    if (!profitSixMonth || profitSixMonth.length !== 6) {
      return Array.from({ length: 6 }, (_, index) => ({
        month: monthLabel(index),
        umsatz: null,
        gewinn: null,
        isSelected: index === 2,
      }));
    }
    return profitSixMonth.map((profitData, index) => ({
      month: monthLabel(index),
      umsatz: sumFilteredProfitField(profitData, filteredUnits, "revenue"),
      gewinn: sumFilteredProfitField(profitData, filteredUnits, "profit"),
      isSelected: index === 2,
    }));
  }, [profitSixMonth, activeMonth, filteredUnits]);

  /**
   * Per-unit current state for "Einheiten im Überblick", joined from the
   * occupancy response the page already holds. See `buildUnitOverviewRows` for
   * why this source and not the month-scoped metrics one.
   */
  const unitOverviewRows = useMemo(
    () => buildUnitOverviewRows(occupancyApi, filteredUnits),
    [occupancyApi, filteredUnits]
  );

  /**
   * How many warnings the server is reporting.
   *
   * FAIL CLOSED. A failed request is `null` and the KPI shows a dash, never 0:
   * "no warnings" is an all-clear, and a dead backend has not earned one. A
   * successful response with an empty list IS 0, and says so.
   */
  const activeWarningCount = useMemo(() => {
    if (upcomingError) return null;
    if (upcomingSnapshot === null) return null;
    return Array.isArray(upcomingSnapshot.warnings)
      ? upcomingSnapshot.warnings.length
      : null;
  }, [upcomingSnapshot, upcomingError]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-[1800px] space-y-4 px-5 py-5 lg:px-6">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <h1 className="m-0 text-[22px] font-semibold leading-tight text-foreground">
              Co-Living Dashboard
            </h1>
            <p className="m-0 mt-1 text-[12px] text-muted-foreground">
              Übersicht zu Belegung, Einheiten und aktuellen Themen
            </p>
          </div>

          {/* The reference shows a date RANGE. This dashboard is month-scoped
              and every number below is priced for one month, so the control
              stays a month picker: a range field that silently means "a month"
              would promise something the page does not compute. */}
          <div className="flex flex-wrap items-end gap-2">
            <FilterSelect
              label="Zeitraum"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
            >
              <option value="lastMonth">Letzter Monat</option>
              <option value="thisMonth">Dieser Monat</option>
              <option value="nextMonth">Nächster Monat</option>
              <option value="customMonth">Monat auswählen</option>
            </FilterSelect>

            {selectedPeriod === "customMonth" && (
              <FilterSelect
                label="Monat"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">Monat wählen</option>
                <option value="2026-01">Jan 2026</option>
                <option value="2026-02">Feb 2026</option>
                <option value="2026-03">Mär 2026</option>
                <option value="2026-04">Apr 2026</option>
                <option value="2026-05">Mai 2026</option>
                <option value="2026-06">Jun 2026</option>
                <option value="2026-07">Jul 2026</option>
                <option value="2026-08">Aug 2026</option>
                <option value="2026-09">Sep 2026</option>
                <option value="2026-10">Okt 2026</option>
                <option value="2026-11">Nov 2026</option>
                <option value="2026-12">Dez 2026</option>
              </FilterSelect>
            )}

            <FilterSelect
              label="Ort"
              value={selectedPlace}
              onChange={(e) => {
                setSelectedPlace(e.target.value);
                setSelectedUnitId("all");
              }}
            >
              <option value="all">Alle Orte</option>
              {placeOptions.map((place) => (
                <option key={place} value={place}>
                  {place}
                </option>
              ))}
            </FilterSelect>

            <FilterSelect
              label="Unit"
              value={selectedUnitId}
              onChange={(e) => setSelectedUnitId(e.target.value)}
            >
              <option value="all">Alle Units</option>
              {allCoLivingUnits
                .filter((unit) => selectedPlace === "all" || unit.place === selectedPlace)
                .map((unit) => (
                  // The `value` stays the technical id — it is what
                  // `selectedUnitId` compares against and what every lookup and
                  // request downstream expects. Only the LABEL is canonical
                  // (Slice 15): the option used to print a UUID.
                  <option key={unit.unitId} value={unit.unitId}>
                    {getUnitHeadingLabel(unit)}
                  </option>
                ))}
            </FilterSelect>
          </div>
        </header>

        {activeMonth > currentBusinessMonthStart() && (
          <p className="m-0 rounded-[10px] border border-amber-500/25 bg-amber-500/10 px-3.5 py-2.5 text-[12px] text-amber-700 dark:text-amber-400">
            Zukunftsmonat gewählt: Diese Werte sind eine Prognose auf Basis aktueller
            Belegungen, Reservierungen und bekannter Kosten.
          </p>
        )}

        {/* ── KPI row ─────────────────────────────────────────────────────
            Six tiles, current OPERATING truth with the inventory context on the
            secondary line. The two populations stay apart: a unit whose lease
            has not started owns rooms, but none that can be let today. */}
        <section
          aria-label="Kennzahlen"
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
        >
          <CoLivingKpiCard
            label="Einheiten im Betrieb"
            value={operating ? operating.unitsCount : null}
            hint={`${dashboard.unitsCount} im Bestand`}
            icon={Building2}
            tone="blue"
          />
          <CoLivingKpiCard
            label="Zimmer im Betrieb"
            value={operating ? operating.totalRooms : null}
            hint={`${dashboard.totalRooms} im Bestand`}
            icon={BedDouble}
            tone="violet"
          />
          <CoLivingKpiCard
            label="Belegt"
            value={operating ? operating.occupiedRooms : null}
            hint="Aktuell belegt"
            icon={Users}
            tone="success"
          />
          <CoLivingKpiCard
            label="Frei"
            value={operating ? operating.freeRooms : null}
            hint="Verfügbare Zimmer"
            icon={DoorOpen}
            tone="neutral"
          />
          <CoLivingKpiCard
            label="Auslastung"
            value={
              operating
                ? occupancyPercentText({
                    total: operating.totalRooms,
                    rate: operating.occupiedRate,
                  })
                : null
            }
            hint={
              operating
                ? `${operating.occupiedRooms} von ${operating.totalRooms} Zimmern`
                : OPERATING_UNAVAILABLE_NOTE
            }
            icon={Gauge}
            tone="success"
            ringPercent={operating ? operating.occupiedRate : null}
          />
          <CoLivingKpiCard
            label="Aktive Warnungen"
            value={activeWarningCount}
            hint="Handlungsbedarf"
            icon={AlertTriangle}
            tone="danger"
          />
        </section>

        {/* ── Warnings, full width ────────────────────────────────────── */}
        <CoLivingWarningsCard snapshot={upcomingSnapshot} error={upcomingError} />

        {/* ── Middle row: occupancy · units · room status ─────────────── */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-[1fr_1.05fr_0.85fr]">
          {/* All three read the shared occupancy contract themselves now
              (OC3), so the page no longer owns a percent formatter at all. The
              donut was the last holdout: its three figures are shares of a
              population rather than an occupancy rate, which is why they were
              left behind once -- but they divide by the same `total`, so a
              portfolio with no rooms in operation printed "0.0%" there too. */}
          <CoLivingOccupancyOverview
            operating={operating}
            unavailableNote={OPERATING_UNAVAILABLE_NOTE}
          />
          <CoLivingUnitsOverview
            rows={unitOverviewRows}
            operating={operating}
            inventoryUnitCount={dashboard.unitsCount}
            unavailableNote={OPERATING_UNAVAILABLE_NOTE}
          />
          <CoLivingRoomStatusDonut
            operating={operating}
            unavailableNote={OPERATING_UNAVAILABLE_NOTE}
          />
        </div>

        {/* ── Bottom row: money over months · upcoming moves ──────────── */}
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[2fr_1fr]">
          <CoLivingMonthlyForecast
            series={monthlySeries}
            formatCurrency={formatCurrency}
            formatChfOrDash={formatChfOrDash}
            monthRevenue={forecast.forecastRevenue}
            monthProfit={forecast.forecastProfit}
            monthCosts={forecast.forecastCosts}
            criticalUnits={forecast.criticalUnits}
            averageRevenuePerRoom={dashboard.averageRevenuePerRoom}
            averageProfitPerUnit={dashboard.averageProfitPerUnit}
          />
          {/* Same snapshot as the warnings panel, second reader — one request,
              two cards (Slice 17). */}
          <CoLivingUpcomingMovesCard
            snapshot={upcomingSnapshot}
            error={upcomingError}
            allowedUnitIds={upcomingMoveUnitIds}
          />
        </div>

        {/* ── Rooms of the selected unit ───────────────────────────────
            NOT in the design reference, and kept deliberately. It is the only
            surface that renders tenancy data, and therefore the only place the
            fail-closed rule is observable: when the tenancy request dies,
            `tenancies` stays null and every room reads "Unbekannt" instead of
            "Frei". Dropping the grid would not just remove a feature, it would
            remove the evidence that a dead request cannot be read as an empty
            house. Placed below the locked layout so the reference's five blocks
            keep their order and proportions. */}
        <section
          aria-label="Raumstatus"
          className="rounded-[10px] border border-border bg-card p-4"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="m-0 text-[13px] font-semibold text-foreground">Raumstatus</h2>
              {/* The status words live in the legend beside this line, and in
                  exactly one place. Repeating a SUBSET of them here is how the
                  surface came to name three of the five words a tile can show
                  (OC4c) — so the subtitle now says where we are, not what the
                  vocabulary is. */}
              <p className="m-0 mt-0.5 text-[11px] text-muted-foreground">
                {firstFilteredUnit
                  ? firstFilteredUnit.place || getUnitHeadingLabel(firstFilteredUnit)
                  : "Status je Zimmer"}
              </p>
            </div>
            <ul className="m-0 flex list-none flex-wrap items-center gap-3 p-0">
              {/* ALL FIVE words `statusLabel` below can produce. "Unbekannt"
                  was missing here since the fail-closed branch was added and
                  "Inaktiv" since ROOM-ACT-5 — a key that omits a word it keys
                  leaves the reader guessing at the one state they did not
                  expect. Each `dot` is the SAME string the tile applies, which
                  T11 pins: a key that disagrees with the thing it keys is
                  worse than no key.

                  The neighbouring "Zimmer-Status" donut and
                  "Belegungsübersicht" legitimately keep three entries — they
                  read the server's occupied/reserved/free counters FOR ROOMS
                  IN OPERATION, which have no inactive and no unknown bucket. */}
              {[
                { name: "Belegt", dot: "bg-emerald-500" },
                { name: "Reserviert", dot: "bg-amber-500" },
                { name: "Frei", dot: "bg-muted-foreground/40" },
                { name: "Unbekannt", dot: "bg-muted-foreground/60" },
                { name: "Inaktiv", dot: INACTIVE_DOT },
              ].map((l) => (
                <li
                  key={l.name}
                  className="flex items-center gap-1.5 text-[10px] text-muted-foreground"
                >
                  <span className={`h-2 w-2 rounded-full ${l.dot}`} aria-hidden />
                  {l.name}
                </li>
              ))}
            </ul>
          </div>

          {tenanciesError ? (
            <p className="m-0 mt-3 text-[11px] leading-[1.5] text-muted-foreground" role="status">
              Belegungsstatus konnte nicht geladen werden.
            </p>
          ) : null}

          {!firstFilteredUnit ? (
            <p className="m-0 mt-3 text-[12px] text-muted-foreground">
              Keine Unit für Raumstatus ausgewählt.
            </p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2.5">
              {rooms
                .filter(
                  (room) =>
                    String(room.unitId || room.unit_id) ===
                    String(firstFilteredUnit.unitId ?? firstFilteredUnit.id)
                )
                .map((room) => {
                  // No `|| "frei"` fallback: that single operator turned every
                  // unknown into an availability claim. `null` reaches its own
                  // branch and reads "Unbekannt".
                  const kind = getRoomOccupancyStatus(room, tenancies);
                  // ACTIVITY FIRST, OCCUPANCY SECOND — and `kind` stays
                  // untouched. A stood-down room is no longer capacity, so
                  // every occupancy word would misdescribe it: "Frei" offers it
                  // to a tenant, "Belegt" counts it as earning. `isActiveRoom`
                  // is the only activity truth (NOT `room.status`: the wire
                  // sends `status or "Frei"`, so a retired room's status reads
                  // "Frei" too). This decides one label and nothing else — the
                  // room keeps its tenancies, its history and its tile.
                  // Same precedence as `presentedRoomStatus` in RoomMap.js and
                  // AdminUnitDetailPage.js, so this page cannot call a room
                  // "Frei" that the unit detail calls "Inaktiv".
                  const retired = !isActiveRoom(room);
                  const tone = retired
                    ? // Neutral, and the most muted of the four: a retired room
                      // must not wear the free tone it wore before. The token
                      // pair is the one `CoLivingRoomCard`'s `inaktiv` entry
                      // and `RoomMap`'s inactive card already use.
                      "border-border bg-muted"
                    : kind === "belegt"
                      ? "border-emerald-500/25 bg-emerald-500/[0.06]"
                      : kind === "reserviert"
                        ? "border-amber-500/25 bg-amber-500/[0.06]"
                        : kind === "frei"
                          ? "border-border bg-muted/40"
                          : "border-border bg-card";
                  const dot = retired
                    ? INACTIVE_DOT
                    : kind === "belegt"
                      ? "bg-emerald-500"
                      : kind === "reserviert"
                        ? "bg-amber-500"
                        : kind === "frei"
                          ? "bg-muted-foreground/40"
                          : "bg-muted-foreground/60";
                  const statusLabel = retired
                    ? "Inaktiv"
                    : kind === "belegt"
                      ? "Belegt"
                      : kind === "reserviert"
                        ? "Reserviert"
                        : kind === "frei"
                          ? "Frei"
                          : "Unbekannt";
                  const unitDetailId = String(
                    firstFilteredUnit.id ?? firstFilteredUnit.unitId ?? ""
                  );
                  return (
                    <Link
                      key={String(room.id ?? room.roomId ?? room.name)}
                      to={`/admin/units/${encodeURIComponent(unitDetailId)}`}
                      className={`block min-w-[150px] flex-1 rounded-[9px] border p-3 no-underline transition-colors hover:border-foreground/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${tone}`}
                    >
                      <span className="mb-1.5 flex items-center gap-1.5 text-[12px] font-medium text-foreground">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} aria-hidden />
                        {room.name || room.roomName || "Zimmer"}
                      </span>
                      <span className="flex items-baseline justify-between text-[11px]">
                        <span className="text-muted-foreground">Status</span>
                        <span className="font-mono font-medium text-foreground">
                          {statusLabel}
                        </span>
                      </span>
                      <span className="flex items-baseline justify-between text-[11px]">
                        <span className="text-muted-foreground">Preis</span>
                        <span className="font-mono text-muted-foreground">
                          {room.price != null && room.price !== ""
                            ? formatChfOrDash(Number(room.price))
                            : OPERATING_DASH}
                        </span>
                      </span>
                    </Link>
                  );
                })}
            </div>
          )}
        </section>

        {dashboard.notStartedUnits > 0 && (
          <p className="m-0 rounded-[10px] border border-amber-500/25 bg-amber-500/10 px-3.5 py-2.5 text-[12px] text-amber-700 dark:text-amber-400">
            Hinweis: {dashboard.notStartedUnits} Unit(s) haben einen zukünftigen Mietstart
            beim Vermieter. Diese laufenden Kosten werden aktuell noch nicht in die
            Live-Ausgaben einberechnet.
          </p>
        )}
      </div>
    </div>
  );
}

export default AdminCoLivingDashboardPage;
